import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from './ui/button';
import ThemeToggle from './ThemeToggle';
import {
  Camera, Images, LayoutGrid, Settings, LogOut, Globe,
  Shield, Calendar, Users, ShoppingCart, Key,
  FileText, Workflow, Sparkles, Menu, X, Radio, UserCheck,
  MessageSquare, CreditCard, Heart, GitCompare, BookOpen,
  Beaker, Cloud, Trophy, HelpCircle,
} from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const navItems = [
  { path: '/dashboard', icon: LayoutGrid, labelKey: 'dashboard.title' },
  { path: '/galleries', icon: Images, labelKey: 'dashboard.stats.galleries' },
  { path: '/clients', icon: Users, pl: 'Klienci', en: 'Clients' },
  { path: '/messages', icon: MessageSquare, pl: 'Wiadomosci', en: 'Messages', hasBadge: true },
  { path: '/orders', icon: ShoppingCart, pl: 'Zamowienia', en: 'Orders' },
  { path: '/bookings', icon: Calendar, pl: 'Rezerwacje', en: 'Bookings' },
  { path: '/contracts', icon: FileText, pl: 'Umowy', en: 'Contracts' },
  { path: '/workflows', icon: Workflow, pl: 'Automatyzacja', en: 'Workflows' },
  { path: '/ai', icon: Sparkles, pl: 'AI Asystent', en: 'AI Assistant' },
  { path: '/live-events', icon: Radio, pl: 'Live Events', en: 'Live Events' },
  { path: '/proofing', icon: UserCheck, pl: 'Live Proofing', en: 'Live Proofing' },
  { path: '/billing', icon: CreditCard, pl: 'Rozliczenia', en: 'Billing' },
  { path: '/weddings', icon: Heart, pl: 'Wesela', en: 'Weddings' },
  { path: '/albums', icon: BookOpen, pl: 'Albumy', en: 'Albums' },
  { path: '/labs', icon: Beaker, pl: 'Laboratoria', en: 'Labs' },
  { path: '/comparisons', icon: GitCompare, pl: 'Before/After', en: 'Before/After' },
  { path: '/cloud', icon: Cloud, pl: 'Focus Cloud', en: 'Focus Cloud' },
  { path: '/sport/admin', icon: Trophy, pl: 'Focus Sport', en: 'Focus Sport' },
  { path: '/settings', icon: Settings, labelKey: 'settings.title' },
  { path: '/help', icon: HelpCircle, pl: 'Pomoc', en: 'Help' },
];

const adminItems = [
  { path: '/config', icon: Key, pl: 'Konfiguracja', en: 'Configuration' },
  { path: '/admin', icon: Shield, pl: 'Panel Admina', en: 'Admin Panel', className: 'text-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/10' },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Sidebar({ activePath }) {
  const { user, logout } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages/unread-count`);
      setUnreadCount(res.data.unread_count || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  const currentPath = activePath || location.pathname;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getLabel = (item) => {
    if (item.labelKey) return t(item.labelKey);
    return language === 'pl' ? item.pl : item.en;
  };

  const isActive = (path) => currentPath === path || currentPath.startsWith(path + '/');

  const renderNav = (onClickItem) => (
    <>
      <Link to="/" className="flex items-center gap-2 mb-10" onClick={onClickItem}>
        <Camera className="w-6 h-6" />
        <span className="font-bold text-lg">Focus Photo</span>
      </Link>

      <nav className="flex-1 space-y-2 overflow-y-auto min-h-0">
        {navItems.map(item => (
          <Link
            key={item.path}
            to={item.path}
            onClick={onClickItem}
            className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              isActive(item.path)
                ? 'bg-secondary text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
            }`}
          >
            <item.icon className="w-5 h-5" />
            {getLabel(item)}
            {item.hasBadge && unreadCount > 0 && (
              <span className="ml-auto bg-primary text-primary-foreground text-xs font-bold rounded-full h-5 min-w-5 flex items-center justify-center px-1.5">
                {unreadCount}
              </span>
            )}
          </Link>
        ))}
        {user?.role === 'admin' && adminItems.map(item => (
          <Link
            key={item.path}
            to={item.path}
            onClick={onClickItem}
            className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              isActive(item.path)
                ? 'bg-secondary text-foreground'
                : item.className || 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
            }`}
          >
            <item.icon className="w-5 h-5" />
            {getLabel(item)}
          </Link>
        ))}
      </nav>

      <div className="space-y-2 pt-6 border-t border-border">
        <div className="flex items-center gap-2 px-2">
          <Button variant="ghost" size="sm" className="flex-1 justify-start" onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}>
            <Globe className="w-4 h-4 mr-2" />{language.toUpperCase()}
          </Button>
          <ThemeToggle />
        </div>
        <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground hover:text-destructive" onClick={handleLogout}>
          <LogOut className="w-4 h-4 mr-2" />{t('auth.logout')}
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-card border-r border-border p-6 hidden lg:flex flex-col z-50">
        {renderNav()}
      </aside>

      {/* Mobile Hamburger */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button variant="outline" size="icon" onClick={() => setMobileOpen(true)}>
          <Menu className="w-5 h-5" />
        </Button>
      </div>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-card border-r border-border p-6 flex flex-col">
            <div className="flex justify-end mb-2">
              <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            {renderNav(() => setMobileOpen(false))}
          </aside>
        </div>
      )}
    </>
  );
}
